import { useState, useCallback, useLayoutEffect, useEffect } from 'react'
import ResizeObserver from 'resize-observer-polyfill'

const useLayoutEffectNoWarning = typeof window !== 'undefined' ? useLayoutEffect : useEffect

const getElementSize = (element, method = 'offset') => {
  if (!element) return { width: undefined, height: undefined }
  if (method === 'offset') return { width: element.offsetWidth, height: element.offsetHeight }
  else if (method === 'client') return { width: element.clientWidth, height: element.clientHeight }
  else if (method === 'scroll') return { width: element.scrollWidth, height: element.scrollHeight }
  else if (method === 'bounding') {
    const { width, height } = element.getBoundingClientRect()
    return { width, height }
  } else throw new Error(method, 'is not a valid element measurement')
}

const isObject = variable =>
  Object.prototype.toString.call(variable) === '[object Object]'

const shallowEqual = (thing1, thing2) => {
  if (thing1 === thing2) return true
  if (!thing1) return false
  if (!thing2) return false
  if (Array.isArray(thing1) && Array.isArray(thing2)) return _shallowEqualArray(thing1, thing2)
  if (isObject(thing1) && isObject(thing2)) return _shallowEqualObject(thing1, thing2)
  return false
}

const _shallowEqualObject = (object1, object2) => {
  const object1Keys = Object.keys(object1)
  const object2Keys = Object.keys(object2)
  if (object1Keys.length !== object2Keys.length) return false
  for (let key in object1) {
    if (!object2.hasOwnProperty(key)) return false
    if (object1[key] !== object2[key]) return false
  }
  return true
}

const _shallowEqualArray = (array1, array2) => {
  if (array1.length !== array2.length) return false
  for (let index in array1) if (array1[index] !== array2[index]) return false
  return true
}

const useShallowSetState = setState => {
  return useCallback(valueOrFn => {
    setState(currentState => {
      const newState = typeof valueOrFn === 'function' ? valueOrFn(currentState) : valueOrFn
      if (shallowEqual(currentState, newState)) return currentState
      else return newState
    })
  }, [setState])
}

const useElementSize = element => {
  const [elementSize, _setElementSize] = useState(getElementSize(element))
  const setElementSize = throttle(useShallowSetState(_setElementSize))

  useLayoutEffectNoWarning(() => {
    const updateSize = () => setElementSize(getElementSize(element))
    if (element && typeof ResizeObserver === 'function') {
      const resizeObserver = new ResizeObserver(updateSize)
      resizeObserver.observe(element)
      updateSize()
      return () => resizeObserver.disconnect(element)
    } else if (element) {
      window.addEventListener('resize', updateSize)
      updateSize()
      return () => window.removeEventListener('resize', updateSize)
    } else updateSize()
  }, [element])

  return elementSize
}

// Defer execution by given value or by if no value given it will use requestAnimationFrame
const deferExec = (fn, timeout = typeof window === 'undefined' ? 16 : undefined) => {
  if (typeof timeout === 'undefined') {
    const timerId = window.requestAnimationFrame(fn)
    return () => window.cancelAnimationFrame(timerId)
  } else {
    const timerId = setTimeout(fn, timeout)
    return () => clearTimeout(timerId)
  }
}

// uncalledFn is an attempt to ensure the last call to the function is always executed
// Not used for now for simplicity
const throttle = (fn, timeout) => {
  let callable = true
  let result
  // let uncalledFn = null

  const callback = () => {
    // if (uncalledFn) uncalledFn()
    callable = true
  }

  return function () {
    const call = () => {
      result = fn.apply(this, arguments)
      callable = false
      // uncalledFn = null
      deferExec(callback, timeout)
    }

    if (callable) call()
    // else uncalledFn = call

    return result
  }
}

export default useElementSize
